import { utils } from '@hh/global.js'
import { fetchWithCache } from './helpers-fetch-cache.js'

const { debounce } = utils

class ProductRecommendations extends HTMLElement {
  constructor() {
    super()

    this.stickyWrapper = document.querySelector('[data-sticky-wrapper]')
    this.debouncedResize = debounce(() => {
      this.calcStickyOffset()
    }, 0).bind(this)

    if (this.dataset.useRebuy === 'true') {
      this._loadAbortController = new AbortController()

      if (this.hasAttribute('loading') && !this._loadingStartedAt) {
        this._loadingStartedAt = Date.now()
      }

      this.fetchRebuyRecommendations()
    } else {
      this.fetchRecommendations()
    }
  }

  connectedCallback() {
    window.addEventListener('resize', this.debouncedResize)
  }

  disconnectedCallback() {
    this._loadAbortController?.abort()
    window.removeEventListener('resize', this.debouncedResize)
  }

  async fetchRecommendations() {
    try {
      const html = await fetchWithCache(
        this.dataset.url,
        {},
        async (response) => {
          const text = await response.text()
          const wrapper = document.createElement('div')
          wrapper.innerHTML = text
          const key = this.dataset.recommendationsKey
          const selector = key
            ? `product-recommendations[data-recommendations-key="${key}"]`
            : 'product-recommendations'
          return wrapper.querySelector(selector)?.innerHTML ?? ''
        }
      )

      if (html.trim().length) {
        this.innerHTML = html
        this.calcStickyOffset()
      }
    } catch (error) {
      console.error(error)
    }
  }

  async fetchRebuyRecommendations() {
    const signal = this._loadAbortController.signal
    const apiKey = this.dataset.apiKey?.trim()
    const limit = parseInt(this.dataset.limit, 10) || 2
    const productId = this.dataset.productId
    const customEndpointId = this.dataset.customEndpointId?.trim()
    const countryCode = this.dataset.countryCode
    const cardSectionId = this.dataset.cardSectionId || 'product-recommendations-rebuy-card'

    if (!apiKey) {
      console.error('Rebuy product recommendations failed: API key is required.')
      this.hideRecommendations()
      return
    }

    if (signal.aborted) return

    const requestContext = {
      apiKey,
      limit,
      productId,
      countryCode,
    }

    let products = []

    if (customEndpointId) {
      try {
        products = await this.fetchRebuyProducts({
          ...requestContext,
          customEndpointId,
          useCustomEndpoint: true,
          signal,
        })
      } catch (error) {
        if (signal.aborted) return
        console.warn('Rebuy custom endpoint failed, falling back to recommended:', error)
      }
    }

    if (!products.length) {
      try {
        products = await this.fetchRebuyProducts({
          ...requestContext,
          customEndpointId: '',
          useCustomEndpoint: false,
          signal,
        })
      } catch (error) {
        if (signal.aborted) return
        console.error('Rebuy product recommendations failed:', error)
        this.hideRecommendations()
        return
      }
    }

    if (signal.aborted) return

    products = this.filterExcludedProducts(products)

    if (!products.length) {
      this.hideRecommendations()
      return
    }

    await this.renderRebuyProducts(products, cardSectionId, signal)
  }

  buildRequestParams({
    apiKey,
    limit,
    productId,
    countryCode,
  }) {
    const params = new URLSearchParams({
      key: apiKey,
      limit: String(limit),
    })

    if (productId) {
      params.set('shopify_product_ids', productId)
    }

    if (countryCode) {
      params.set('country_code', countryCode)
    }

    return params
  }

  async fetchRebuyProducts({
    apiKey,
    limit,
    productId,
    customEndpointId,
    countryCode,
    useCustomEndpoint,
    signal,
  }) {
    const params = this.buildRequestParams({
      apiKey,
      limit,
      productId,
      countryCode,
    })

    const url =
      useCustomEndpoint && customEndpointId
        ? `https://rebuyengine.com/api/v1/custom/id/${encodeURIComponent(customEndpointId)}?${params}`
        : `https://rebuyengine.com/api/v1/products/recommended?${params}`

    return fetchWithCache(url, { signal }, async (response) => {
      const data = await response.json()

      if (data.error) {
        throw new Error(data.error)
      }

      return data.data || []
    })
  }

  dedupeProducts(products) {
    const seen = new Set()

    return products.filter((product) => {
      const id = product?.id ? String(product.id) : product?.handle

      if (!id || seen.has(id)) {
        return false
      }

      seen.add(id)
      return true
    })
  }

  filterExcludedProducts(products) {
    const currentProductId = this.dataset.productId ? String(this.dataset.productId) : ''

    return this.dedupeProducts(products).filter((product) => {
      const productId = product?.id ? String(product.id) : ''

      if (currentProductId && productId === currentProductId) {
        return false
      }

      return Boolean(product?.handle)
    })
  }

  async waitForLoadingPaint() {
    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    })

    const minLoadingMs = 500
    const elapsed = Date.now() - (this._loadingStartedAt || 0)
    const remaining = minLoadingMs - elapsed

    if (remaining > 0) {
      await new Promise((resolve) => setTimeout(resolve, remaining))
    }
  }

  async renderRebuyProducts(products, cardSectionId, signal) {
    const cardResults = await Promise.all(
      products.map(async (product) => {
        const html = await this.fetchProductCard(product.handle, cardSectionId, signal)

        if (!html || signal.aborted) return null

        return html
      })
    )

    if (signal.aborted) return

    const validCards = cardResults.filter(Boolean)

    if (!validCards.length) {
      this.hideRecommendations()
      return
    }

    await this.waitForLoadingPaint()

    if (signal.aborted) return

    const heading = this.querySelector(
      '.product-recommendations__heading, .size-guide-drawer__recos-title'
    )
    const headingHtml = heading ? heading.outerHTML : ''
    const list = document.createElement('ul')
    const listClass = this.dataset.listClass || 'product-recommendations__items'
    const itemClass = this.dataset.itemClass || 'product-recommendations__item'

    list.setAttribute('role', 'list')
    list.className = listClass

    validCards.forEach((html) => {
      const item = document.createElement('li')
      item.className = itemClass
      item.innerHTML = html
      list.appendChild(item)
    })

    this.innerHTML = `${headingHtml}${list.outerHTML}`
    this.removeAttribute('loading')
    this.removeAttribute('aria-busy')
    this.classList.remove('product-recommendations--loading')
    this.calcStickyOffset()
  }

  async fetchProductCard(handle, sectionId, signal) {
    if (!handle) return null

    try {
      return await fetchWithCache(
        `/products/${encodeURIComponent(handle)}?section_id=${sectionId}`,
        { signal },
        async (response) => {
          const text = await response.text()
          const doc = new DOMParser().parseFromString(text, 'text/html')
          const card = doc.querySelector('[data-rebuy-product-card]')
          return card ? card.innerHTML : null
        }
      )
    } catch (error) {
      if (signal?.aborted) return null
      console.error(`Failed to fetch Rebuy product card for ${handle}:`, error)
      return null
    }
  }

  hideRecommendations() {
    this.removeAttribute('loading')
    this.removeAttribute('aria-busy')
    this.classList.remove('product-recommendations--loading')
    this.innerHTML = ''
    this.hidden = true
    this.calcStickyOffset()
  }

  calcStickyOffset() {
    if (!this.stickyWrapper || !this.classList.contains('product__recommendations')) return

    const isMobile = window.matchMedia('(max-width: 989px)').matches
    this.stickyWrapper.style.paddingBottom = isMobile
      ? '0px'
      : `${this.offsetHeight}px`
  }
}

customElements.define('product-recommendations', ProductRecommendations)
